import SpotlightButton from '../ui/SpotlightButton'

interface Plan {
  name: string
  subtitle: string
  price: string
  originalPrice?: string
  offLabel?: string
  description: string
  cash: string
  time: string
  limit: string
  features: string[]
  isPopular?: boolean
  note?: string
  buttonText?: string
  hue?: number
  discount?: string
}

const plans: Plan[] = [
  {
    name: 'Básico',
    subtitle: 'Para pequeños productores.',
    price: '$289.000',
    originalPrice: 'COP 589.000',
    offLabel: '¡50% OFF!',
    description: 'Por solo $25.000/mes',
    cash: 'COP',
    time: 'Por Año  ',
    limit: 'Menos de 100 cerdos',
    features: [
      '2 Colaboradores',
      'Reportes Diarios',
      'Registro de Cuentas',
      'Gestión de Inventario',
      'Programa de Actividades',
      'Informes de Rendimiento',
    ],
    buttonText: 'Contáctenos',
    hue: 4000,
    discount: 'OFERTA LIMITADA',
  },
  {
    name: 'Intermedio',
    subtitle: 'Para granjas en crecimiento.',
    price: '$489.000',
    originalPrice: 'COP 989.000',
    offLabel: '¡50% OFF!',
    description: 'Ahorra hasta el 20%',
    cash: 'COP',
    time: 'Por Año',
    limit: '100 a 300 cerdos',
    features: [
      'Todo en Básico.',
      '4 Colaboradores',
      'Consultas a Inteligencia Artificial',
      'Informes Financieros',
    ],
    discount: 'OFERTA LIMITADA',
    isPopular: true,
    buttonText: 'Contáctenos',
    hue: 4000,
  },
  {
    name: 'Enterprise',
    subtitle: 'Escalabilidad total.',
    price: '',
    description: '',
    cash: 'Contactar con el equipo de ventas.',
    time: 'Más de 300 cerdos',
    limit: 'Más de 300 cerdos',
    features: [
      'Todo en Intermedio.',
      'Colaboradores Ilimitados',
      'Integración Personalizada',
      'Programa de Actividades Personalizados',
    ],
    note: '',
    buttonText: 'Contáctenos',
    hue: 4000,
  },
]

const PlansSection = () => {
  const getWhatsAppLink = (planName: string) => {
    const message = `Hola quiero obtener el plan ${planName.toLowerCase()}`
    return `https://wa.me/573168751263?text=${encodeURIComponent(message)}`
  }

  return (
    <section id="planes" className="py-20 relative -mt-[100px] z-10">
      <div className="max-w-[1200px] mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-[30px] max-w-[500px] lg:max-w-none mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`bg-white p-10 rounded-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.1)] relative flex flex-col transition-all duration-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(255,63,52,0.3),0_0_80px_rgba(255,63,52,0.15)] ${
                plan.isPopular ? 'scale-105 z-[2]' : ''
              }`}
            >
              {(plan.isPopular || plan.discount) && (
                <div className={`absolute -top-[20px] left-1/2 -translate-x-1/2 px-3 py-3 rounded-[20px] text-[0.8rem] font-bold uppercase flex items-center gap-1.5 whitespace-nowrap ${
                  plan.discount ? 'bg-[#FDDB45] text-[#856404]' : 'bg-[#FF3F34] text-white'
                }`}>
                  {plan.discount && (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#856404" className="w-5 h-5">
                      <path d="M12 5.25C16.8325 5.25 20.75 9.16751 20.75 14C20.75 18.8325 16.8325 22.75 12 22.75C7.16751 22.75 3.25 18.8325 3.25 14C3.25 9.16751 7.16751 5.25 12 5.25ZM12 6.75C7.99594 6.75 4.75 9.99594 4.75 14C4.75 18.0041 7.99594 21.25 12 21.25C16.0041 21.25 19.25 18.0041 19.25 14C19.25 9.99594 16.0041 6.75 12 6.75ZM12 9.25C12.4142 9.25 12.75 9.58579 12.75 10V14C12.75 14.4142 12.4142 14.75 12 14.75C11.5858 14.75 11.25 14.4142 11.25 14V10C11.25 9.58579 11.5858 9.25 12 9.25ZM15 1.25C15.4142 1.25 15.75 1.58579 15.75 2C15.75 2.41421 15.4142 2.75 15 2.75H9C8.58579 2.75 8.25 2.41421 8.25 2C8.25 1.58579 8.58579 1.25 9 1.25H15Z" />
                    </svg>
                  )}
                  {plan.discount || 'POPULAR'}
                </div>
              )}

              <h3 className="text-2xl font-bold mb-1 text-center">{plan.name}</h3>
              <p className="text-[#888] text-[0.9rem] mb-5 text-center">{plan.subtitle}</p>

              {plan.originalPrice && (
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-[#999] text-[0.95rem] line-through">{plan.originalPrice}</span>
                  {plan.offLabel && (
                    <span className="bg-[#FFD700] text-[#333] text-[0.7rem] font-bold px-2 py-0.5 rounded-full">
                      {plan.offLabel}
                    </span>
                  )}
                </div>
              )}

              <div className="text-[2rem] font-bold mb-0 flex items-baseline justify-center gap-2">
                <span className="text-base font-semibold text-[#666]">{plan.cash}</span>
                {plan.price}
              </div>
              {plan.time && (
                <p className="text-[0.9rem] font-semibold text-[#666] text-center">{plan.time}</p>
              )}
              <div className="min-h-[2.5rem] flex items-start justify-center mb-3">
                {plan.description && (
                  <p
                    className={`text-[0.75rem] text-[#618C4C] font-bold px-3 py-1.5 rounded-[10px] mt-2 flex items-center gap-1.5`}
                    style={{ backgroundColor: `#DDFED7` }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 flex-shrink-0">
                      <path d="M12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12C1.25 6.06294 6.06294 1.25 12 1.25ZM12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75ZM16.4678 7.96973C16.7606 7.67694 17.2354 7.67715 17.5283 7.96973C17.8209 8.26264 17.8211 8.73747 17.5283 9.03027L10.5283 16.0303C10.2355 16.323 9.76067 16.3229 9.46777 16.0303L6.46777 13.0303C6.17496 12.7374 6.17496 12.2626 6.46777 11.9697C6.76065 11.6769 7.23542 11.6769 7.52832 11.9697L9.99805 14.4395L16.4678 7.96973Z" />
                    </svg>
                    {plan.description}
                  </p>
                )}
              </div>
              {plan.note && (
                <p className="text-[0.8rem] text-[#555] mb-1">
                  Después de 500 <strong>$ 2.000 cop</strong>
                </p>
              )}
              <span
                className={`inline-flex items-center gap-1.5 self-center px-3 py-1 rounded-[10px] text-[0.85rem] mb-5 ${
                  plan.name === 'Premium'
                    ? 'bg-[#FFF5F5] text-red-600'
                    : 'bg-[#FFF0F0] text-[#FF3F34]'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 flex-shrink-0">
                  <path d="M21.8304 3.03027C21.9708 4.05118 22.0552 5.60959 21.8392 6.96484C21.7315 7.64032 21.5432 8.30441 21.221 8.83008C21.0103 9.1738 20.7319 9.46768 20.3792 9.66016C21.5282 11.7725 22.2284 14.4273 21.4612 16.7246C20.6032 19.2936 18.0038 21.182 12.8655 21.46L12.8353 21.4619L12.806 21.46C10.3397 21.2957 7.93349 21.0468 6.18097 20.1211C5.28854 19.6496 4.54966 18.9953 4.05792 18.0811C3.56801 17.1701 3.34498 16.0405 3.41437 14.6514C3.51395 12.6597 4.01791 10.9724 4.77569 9.58105C3.67542 8.88008 3.21709 7.68178 3.08038 6.52246C2.92496 5.20361 3.16464 3.82578 3.43097 2.95312L3.5462 2.57812L3.93683 2.59961C6.36237 2.73444 7.8561 3.24583 8.72491 3.90234C9.24144 4.29276 9.53932 4.74128 9.66437 5.17578C10.6416 4.78188 11.6224 4.58303 12.512 4.58301C13.5689 4.58304 14.6013 4.75885 15.5638 5.11719C15.8128 4.73041 16.2391 4.32105 16.8558 3.94922C17.7846 3.38928 19.1975 2.87352 21.2708 2.60254L21.763 2.53809L21.8304 3.03027ZM12.512 5.58301C11.5869 5.58303 10.5004 5.83694 9.42511 6.37695C8.11258 7.03622 6.83226 8.11404 5.89288 9.64453C5.07966 10.9696 4.51619 12.6453 4.41339 14.7012C4.35061 15.9579 4.55599 16.8957 4.93878 17.6074C5.31979 18.3157 5.89777 18.8396 6.64874 19.2363C8.17617 20.0431 10.3521 20.2925 12.8362 20.459C17.7493 20.1877 19.8401 18.4225 20.513 16.4082C21.2112 14.3175 20.477 11.7197 19.2181 9.64258C18.6829 8.75963 18.0672 7.99659 17.4622 7.43457C16.9076 6.91945 16.2841 6.51338 15.6116 6.21484C14.6618 5.79321 13.6092 5.58305 12.512 5.58301ZM12.5091 13.6826C14.5179 13.6828 16.1466 14.7197 16.1468 15.998L16.1419 16.1172C16.0441 17.34 14.455 18.3123 12.5091 18.3125C10.5631 18.3125 8.97408 17.3401 8.87628 16.1172L8.8714 15.998C8.87163 14.7197 10.5002 13.6827 12.5091 13.6826ZM12.5091 14.4824C11.6337 14.4824 10.878 14.7099 10.3665 15.0352C9.84572 15.3666 9.67132 15.7272 9.6712 15.998C9.67139 16.2689 9.84583 16.6286 10.3665 16.96C10.878 17.2853 11.6335 17.5127 12.5091 17.5127C13.3846 17.5126 14.1403 17.2852 14.6517 16.96C15.172 16.6287 15.3468 16.2688 15.347 15.998C15.3469 15.7272 15.1723 15.3665 14.6517 15.0352C14.1403 14.7099 13.3845 14.4825 12.5091 14.4824ZM11.1819 15.668C11.3646 15.668 11.513 15.9638 11.513 16.3291C11.513 16.6944 11.3646 16.9902 11.1819 16.9902C10.9994 16.99 10.8519 16.6943 10.8519 16.3291C10.8519 15.9639 10.9994 15.6682 11.1819 15.668ZM13.8304 15.668C14.013 15.668 14.1614 15.9638 14.1614 16.3291C14.1614 16.6944 14.013 16.9902 13.8304 16.9902C13.6478 16.99 13.5003 16.6943 13.5003 16.3291C13.5003 15.9639 13.6478 15.6682 13.8304 15.668ZM9.2005 10.376C9.56571 10.3761 9.86163 10.8203 9.86163 11.3682C9.86147 11.9158 9.56562 12.3592 9.2005 12.3594C8.8353 12.3594 8.53952 11.9159 8.53937 11.3682C8.53937 10.8202 8.8352 10.376 9.2005 10.376ZM15.8138 10.376C16.179 10.3761 16.4749 10.8203 16.4749 11.3682C16.4748 11.9158 16.1789 12.3592 15.8138 12.3594C15.4486 12.3594 15.1528 11.9159 15.1526 11.3682C15.1526 10.8202 15.4485 10.376 15.8138 10.376ZM20.8978 3.66992C19.2064 3.93954 18.0851 4.37609 17.3724 4.80566C16.9227 5.07676 16.6512 5.33441 16.4935 5.5332C17.0841 5.84477 17.6388 6.23298 18.1429 6.70117C18.7462 7.26159 19.3407 7.98287 19.8685 8.79883C20.0557 8.70644 20.2216 8.54714 20.3685 8.30762C20.5936 7.94034 20.7539 7.4217 20.8519 6.80762C21.0155 5.78104 20.9884 4.59442 20.8978 3.66992ZM4.28448 3.62402C4.0949 4.39329 3.95884 5.43169 4.07355 6.40527C4.19064 7.39826 4.55313 8.24066 5.29718 8.72656C6.24952 7.3255 7.46618 6.29296 8.73077 5.61035C8.71401 5.38515 8.58496 5.0499 8.12237 4.7002C7.51888 4.24413 6.36479 3.78478 4.28448 3.62402Z" />
                </svg>
                {plan.limit}
              </span>

              <ul className="mt-5 mb-[30px] flex-grow">
                {plan.features.map((feature) => (
                  <li key={feature} className="mb-3 text-[0.95rem] text-[#555] flex items-start gap-2.5">
                    <span
                      className="flex-shrink-0 w-4 h-4 mt-0.5 bg-no-repeat bg-center bg-contain"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' width='16' title='check' fill='%23FF3F34'%3E%3Cpath d='M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z' /%3E%3C/svg%3E")`
                      }}
                    />
                    {feature}
                  </li>
                ))}
              </ul>

              <a href={getWhatsAppLink(plan.name)} target="_blank" rel="noopener noreferrer">
                <SpotlightButton hue={plan.hue} className="w-full">
                  {plan.buttonText}
                </SpotlightButton>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default PlansSection
